import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '../../../Context/ThemeContext';
import { TutorialManagementHandler, TutorialListItem } from './TutorialManagementHandler';
import TutorialFormModal from './TutorialFormModal';

const CATEGORY_LABELS: Record<string, string> = {
    'introduction': 'Tutorials: Introduction',
    'getting-started': 'Tutorials: Getting Started',
    'vs-friends': 'Tutorials: Vs Friends',
    'how-to-play': 'Tutorials: How to Play',
    'beginners-basics': 'Beginners Guide: Sudoku Basics',
    'beginners-rules': 'Beginners Guide: Game Rules',
};

const categoryLabel = (value: string): string => CATEGORY_LABELS[value] || value || '-';

// Pull the YouTube id from any of the accepted URL shapes for a compact display.
const videoIdOf = (url: string | undefined): string => {
    if (!url) return '-';
    const shorts = url.match(/shorts\/([^?&/\s]+)/);
    if (shorts) return shorts[1];
    const general = url.match(/(?:embed\/|v=|youtu\.be\/)([^?&/\s]+)/);
    return general ? general[1] : url;
};

type SortKey = 'category' | 'title' | 'priority' | 'active' | 'languageCount';
type SortDir = 'asc' | 'desc';
type StatusFilter = 'all' | 'active' | 'hidden';

const TutorialListPage: React.FC = () => {
    const { theme, isMobile } = useTheme();

    const [tutorials, setTutorials] = useState<TutorialListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);

    const [deleteConfirm, setDeleteConfirm] = useState<TutorialListItem | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Filter + sort controls
    const [search, setSearch] = useState('');
    const [sectionFilter, setSectionFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [sortKey, setSortKey] = useState<SortKey>('category');
    const [sortDir, setSortDir] = useState<SortDir>('asc');

    const fetchTutorials = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const handler = new TutorialManagementHandler();
            const response = await handler.getTutorials();
            setTutorials(response?.results || []);
        } catch (err) {
            console.error('Error fetching tutorial videos:', err);
            setError('Failed to load tutorial videos.');
            setTutorials([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTutorials();
    }, [fetchTutorials]);

    const handleAdd = () => {
        setEditingId(null);
        setShowModal(true);
    };

    const handleEdit = (id: string) => {
        setEditingId(id);
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setEditingId(null);
    };

    const handleModalSaved = () => {
        setShowModal(false);
        setEditingId(null);
        fetchTutorials();
    };

    const handleDeleteExecute = async () => {
        if (!deleteConfirm?.id) return;
        setDeleteLoading(true);
        try {
            const handler = new TutorialManagementHandler();
            await handler.deleteTutorial(deleteConfirm.id);
            setDeleteConfirm(null);
            fetchTutorials();
        } catch (err: any) {
            console.error('Error deleting tutorial video:', err);
            setError(err?.response?.data?.message || err?.message || 'Failed to delete tutorial video.');
            setDeleteConfirm(null);
        } finally {
            setDeleteLoading(false);
        }
    };

    const toggleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    const clearFilters = () => {
        setSearch('');
        setSectionFilter('all');
        setStatusFilter('all');
    };

    // Derived list: filter then sort (data set is small, so client-side is fine).
    const displayed = useMemo(() => {
        const term = search.trim().toLowerCase();
        const filtered = tutorials.filter((v) => {
            if (sectionFilter !== 'all' && v.category !== sectionFilter) return false;
            if (statusFilter === 'active' && !v.active) return false;
            if (statusFilter === 'hidden' && v.active) return false;
            if (term && !(v.title || '').toLowerCase().includes(term)) return false;
            return true;
        });

        const tiebreak = (a: TutorialListItem, b: TutorialListItem): number =>
            (a.category || '').localeCompare(b.category || '') || (a.priority || 0) - (b.priority || 0);

        const sorted = [...filtered].sort((a, b) => {
            let cmp = 0;
            switch (sortKey) {
                case 'title': cmp = (a.title || '').localeCompare(b.title || ''); break;
                case 'category': cmp = (a.category || '').localeCompare(b.category || ''); break;
                case 'priority': cmp = (a.priority || 0) - (b.priority || 0); break;
                case 'active': cmp = Number(a.active) - Number(b.active); break;
                case 'languageCount': cmp = (a.languageCount || 0) - (b.languageCount || 0); break;
                default: cmp = 0;
            }
            if (cmp === 0) cmp = tiebreak(a, b);
            return sortDir === 'asc' ? cmp : -cmp;
        });

        return sorted;
    }, [tutorials, search, sectionFilter, statusFilter, sortKey, sortDir]);

    const filtersActive = search.trim() !== '' || sectionFilter !== 'all' || statusFilter !== 'all';

    const styles: Record<string, React.CSSProperties> = {
        container: { padding: 0 },
        headerRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            flexWrap: 'wrap' as const,
            gap: '12px',
        },
        title: {
            fontSize: isMobile ? '22px' : '28px',
            fontWeight: 700,
            color: theme.titleColor,
            marginBottom: '4px',
        },
        subtitle: { fontSize: '14px', color: theme.textSecondary },
        addButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            color: '#fff',
            background: '#3b82f6',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
        },
        controlsRow: {
            display: 'flex',
            flexWrap: 'wrap' as const,
            gap: '12px',
            alignItems: 'center',
            marginBottom: '16px',
        },
        searchInput: {
            flex: '1 1 220px',
            minWidth: '180px',
            padding: '9px 14px',
            fontSize: '14px',
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: '8px',
            background: theme.inputBg,
            color: theme.text,
            outline: 'none',
            boxSizing: 'border-box' as const,
        },
        filterSelect: {
            padding: '9px 12px',
            fontSize: '14px',
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: '8px',
            background: theme.inputBg,
            color: theme.text,
            outline: 'none',
            cursor: 'pointer',
        },
        clearButton: {
            padding: '9px 14px',
            fontSize: '13px',
            fontWeight: 600,
            color: theme.text,
            background: 'transparent',
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            cursor: 'pointer',
        },
        resultCount: {
            fontSize: '13px',
            color: theme.textSecondary,
            marginLeft: 'auto',
        },
        tableContainer: {
            background: theme.card,
            borderRadius: '10px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
        tableWrapper: { overflowX: 'auto' as const },
        table: {
            width: '100%',
            borderCollapse: 'collapse' as const,
            tableLayout: 'fixed' as const,
            minWidth: isMobile ? '760px' : 'auto',
        },
        th: {
            padding: '14px 16px',
            textAlign: 'center' as const,
            fontWeight: 600,
            fontSize: '13px',
            color: theme.textSecondary,
            background: theme.tableHeader,
            borderBottom: `1px solid ${theme.border}`,
            whiteSpace: 'nowrap' as const,
        },
        thSortable: {
            cursor: 'pointer',
            userSelect: 'none' as const,
        },
        td: {
            padding: '14px 16px',
            textAlign: 'center' as const,
            fontSize: '14px',
            color: theme.text,
            borderBottom: `1px solid ${theme.border}`,
        },
        actionButton: {
            padding: '5px 10px',
            fontSize: '12px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '6px',
        },
        badge: {
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: '10px',
            fontSize: '11px',
            fontWeight: 600,
        },
        spinner: {
            width: '24px',
            height: '24px',
            border: '2px solid rgba(59, 130, 246, 0.3)',
            borderTop: '2px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
        },
        loadingContainer: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 0',
            gap: '16px',
        },
        errorBanner: {
            padding: '12px 16px',
            borderRadius: '8px',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#ef4444',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        emptyRow: {
            textAlign: 'center' as const,
            padding: '40px',
            color: theme.textSecondary,
            fontStyle: 'italic' as const,
        },
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        confirmModal: {
            background: theme.card,
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        },
        confirmTitle: { fontSize: '18px', fontWeight: 700, color: theme.text, marginBottom: '12px' },
        confirmText: { fontSize: '14px', color: theme.textSecondary, marginBottom: '20px', lineHeight: 1.5 },
        confirmActions: { display: 'flex', justifyContent: 'flex-end', gap: '12px' },
    };

    const statusBadge = (active: boolean): React.CSSProperties =>
        active
            ? { ...styles.badge, background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }
            : { ...styles.badge, background: 'rgba(107, 114, 128, 0.2)', color: '#6b7280' };

    const arrow = (key: SortKey): string => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

    const sortableTh = (key: SortKey, label: string, width: string, align: 'left' | 'center' = 'center') => (
        <th
            style={{ ...styles.th, ...styles.thSortable, width, textAlign: align }}
            onClick={() => toggleSort(key)}
            title="Click to sort"
        >
            {label}{arrow(key)}
        </th>
    );

    return (
        <div style={styles.container}>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>

            <div style={styles.headerRow}>
                <div>
                    <h1 style={styles.title}>Tutorials</h1>
                    <p style={styles.subtitle}>Manage the tutorial and Beginners-Guide videos shown on the website</p>
                </div>
                <button
                    style={styles.addButton}
                    onClick={handleAdd}
                    onMouseEnter={(e) => { (e.target as HTMLElement).style.background = '#2563eb'; }}
                    onMouseLeave={(e) => { (e.target as HTMLElement).style.background = '#3b82f6'; }}
                >
                    + Add Tutorial Video
                </button>
            </div>

            {error && (
                <div style={styles.errorBanner}>
                    <span>{error}</span>
                    <button
                        onClick={fetchTutorials}
                        style={{ padding: '4px 12px', fontSize: '13px', fontWeight: 600, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                        Retry
                    </button>
                </div>
            )}

            <div style={styles.controlsRow}>
                <input
                    type="text"
                    placeholder="Search by title..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={styles.searchInput}
                    aria-label="Search tutorials by title"
                />
                <select
                    value={sectionFilter}
                    onChange={(e) => setSectionFilter(e.target.value)}
                    style={styles.filterSelect}
                    aria-label="Filter by section"
                >
                    <option value="all">All Sections</option>
                    {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                    ))}
                </select>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                    style={styles.filterSelect}
                    aria-label="Filter by status"
                >
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="hidden">Hidden</option>
                </select>
                {filtersActive && (
                    <button style={styles.clearButton} onClick={clearFilters}>Clear</button>
                )}
                <span style={styles.resultCount}>
                    {displayed.length} of {tutorials.length}
                </span>
            </div>

            <div style={styles.tableContainer}>
                <div style={styles.tableWrapper}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                {sortableTh('title', 'Title', '24%', 'left')}
                                {sortableTh('category', 'Section', '24%')}
                                <th style={{ ...styles.th, width: '14%' }}>Video</th>
                                {sortableTh('active', 'Status', '9%')}
                                {sortableTh('priority', 'Priority', '8%')}
                                {sortableTh('languageCount', 'Languages', '9%')}
                                <th style={{ ...styles.th, width: '12%' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} style={styles.td}>
                                        <div style={styles.loadingContainer}>
                                            <div style={styles.spinner} />
                                            <span style={{ color: theme.textSecondary }}>Loading tutorial videos...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : tutorials.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ ...styles.td, ...styles.emptyRow }}>
                                        No tutorial videos found. Click &quot;Add Tutorial Video&quot; to create one.
                                    </td>
                                </tr>
                            ) : displayed.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ ...styles.td, ...styles.emptyRow }}>
                                        No videos match your filters.
                                    </td>
                                </tr>
                            ) : (
                                displayed.map((video) => (
                                    <tr
                                        key={video.id}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = theme.tableRowHover; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                    >
                                        <td style={{ ...styles.td, fontWeight: 600, textAlign: 'left' }}>{video.title || '-'}</td>
                                        <td style={{ ...styles.td, color: theme.textSecondary, fontSize: '13px' }}>{categoryLabel(video.category)}</td>
                                        <td style={{ ...styles.td, color: theme.textSecondary, fontSize: '12px', fontFamily: 'monospace' }}>{videoIdOf(video.videoUrl)}</td>
                                        <td style={styles.td}>
                                            <span style={statusBadge(video.active)}>{video.active ? 'Active' : 'Hidden'}</span>
                                        </td>
                                        <td style={styles.td}>{video.priority}</td>
                                        <td style={styles.td}>{video.languageCount}</td>
                                        <td style={styles.td}>
                                            <button
                                                style={{ ...styles.actionButton, background: '#3b82f6', color: '#fff' }}
                                                onClick={() => handleEdit(video.id)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                style={{ ...styles.actionButton, background: '#ef4444', color: '#fff' }}
                                                onClick={() => setDeleteConfirm(video)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <TutorialFormModal
                    tutorialId={editingId}
                    onClose={handleModalClose}
                    onSaved={handleModalSaved}
                />
            )}

            {deleteConfirm && (
                <div style={styles.overlay} onClick={() => !deleteLoading && setDeleteConfirm(null)}>
                    <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.confirmTitle}>Delete Tutorial Video</div>
                        <div style={styles.confirmText}>
                            Are you sure you want to delete <strong>&quot;{deleteConfirm.title}&quot;</strong>?
                            This action cannot be undone and it will immediately stop showing on the website.
                        </div>
                        <div style={styles.confirmActions}>
                            <button
                                style={{
                                    padding: '8px 16px',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: theme.text,
                                    background: 'transparent',
                                    border: `1px solid ${theme.border}`,
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                }}
                                onClick={() => setDeleteConfirm(null)}
                                disabled={deleteLoading}
                            >
                                Cancel
                            </button>
                            <button
                                style={{
                                    padding: '8px 16px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#fff',
                                    background: '#ef4444',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    opacity: deleteLoading ? 0.6 : 1,
                                }}
                                onClick={handleDeleteExecute}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? 'Deleting...' : 'Delete Video'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TutorialListPage;
